import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import {
  Badge,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator
} from 'components/common';
import { BarChart } from 'components/common/charts';
import { Form, RadioGroup } from 'components/common/form';

interface Props {
  chartData: { title: string; [x: string]: string | number }[];
  loading: boolean;
  numberOfUsers: number;
  selectedItems: string[];
  setSelectedItems: (value: string[]) => void;
  showPercentages: boolean;
}

const AnalyticsCompetenciesChart = ({
  chartData,
  loading,
  numberOfUsers,
  selectedItems,
  setSelectedItems,
  showPercentages
}: Props) => {
  const { messages, theme } = useReduxState();

  const [graphView, setGraphView] = useState('both');
  const [yAxisDataKey, setYAxisDataKey] = useState([]);

  useEffect(() => {
    if (graphView === 'competencies')
      setYAxisDataKey([
        {
          label: messages.analyticsCompetencies,
          color: theme.successStandaloneColor
        }
      ]);
    else if (graphView === 'ambitions')
      setYAxisDataKey([
        {
          label: messages.analyticsAmbitions,
          color: theme.textColorLighter
        }
      ]);
    else
      setYAxisDataKey([
        {
          label: messages.analyticsCompetencies,
          color: theme.successStandaloneColor
        },
        {
          label: messages.analyticsAmbitions,
          color: theme.textColorLighter
        }
      ]);
  }, [graphView]);

  const handleClearItem = (item) => {
    setSelectedItems(
      selectedItems.filter((selecteItem) => selecteItem !== item)
    );
  };

  return (
    <div className="chart">
      {chartData.length ? (
        <div>
          <FlexContainer
            style={{
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              padding: '5px 10px 10px'
            }}
          >
            <div
              style={{
                color: theme.textColorLight,
                fontSize: '1.6rem',
                fontWeight: 600
              }}
            >
              {selectedItems.length ? (
                messages.analyticsCompetenciesCustom
              ) : (
                <div>
                  {messages.analyticsCompetenciesTop}
                  <div
                    style={{
                      color: theme.textColorLighter,
                      fontSize: '1.2rem',
                      fontWeight: 400
                    }}
                  >
                    {messages.analyticsCompetenciesTopInfo}
                  </div>
                </div>
              )}
            </div>

            <Form>
              <RadioGroup
                field="graphDisplay"
                initialValue={graphView}
                inline={true}
                onChange={(value) => setGraphView(value)}
                options={[
                  { id: 'both', label: messages.commonShowAll, value: 'both' },
                  {
                    id: 'competencies',
                    label: messages.analyticsCompetencies,
                    value: 'competencies'
                  },
                  {
                    id: 'ambitions',
                    label: messages.analyticsAmbitions,
                    value: 'ambitions'
                  }
                ]}
              />
            </Form>
          </FlexContainer>

          {selectedItems?.length ? (
            <FlexContainer
              style={{ flexWrap: 'wrap', padding: '0px 10px 5px' }}
            >
              {selectedItems.map((item) => (
                <div
                  style={{ display: 'inline-block', padding: '4px 10px 4px 0' }}
                  key={item}
                >
                  <Badge
                    isRounded={false}
                    onClose={() => handleClearItem(item)}
                    text={item}
                  />
                </div>
              ))}
            </FlexContainer>
          ) : null}

          {loading ? (
            <LoadingIndicator />
          ) : (
            <BarChart
              data={chartData}
              tooltipFormatter={(value, name, props) =>
                showPercentages ? (
                  <span>
                    {`${value} %`}
                    <span
                      style={{
                        color: theme.textColorLighter,
                        fontWeight: 600,
                        marginLeft: 5
                      }}
                    >
                      {`(${
                        name === messages.analyticsAmbitions
                          ? props.payload.ambitionsCount
                          : props.payload.competenciesCount
                      }/${numberOfUsers} ${messages.usersFiltersUsers})`}
                    </span>
                  </span>
                ) : (
                  `${value} ${messages.usersFiltersUsers}`
                )
              }
              xAxisDataKey="title"
              yAxisDataKey={yAxisDataKey}
              {...(showPercentages
                ? { yAxisDomain: [0, 100], yUnit: '%' }
                : {})}
            />
          )}
        </div>
      ) : (
        <EmptyIndicator icon="chart-bar" title={messages.commonNoData} />
      )}
    </div>
  );
};

export default AnalyticsCompetenciesChart;
